import { Button } from '@workspace/ui/components/button'
import { cn } from '@workspace/ui/lib/utils'
import { ArrowLeft, ArrowRight, Expand, FileText, Maximize2, Minimize2, Shrink } from 'lucide-react'
import * as pdfjs from 'pdfjs-dist'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'

type FitMode = 'page' | 'width'

export interface PdfViewerProps {
  url: string
  className?: string
  footerAction?: React.ReactNode
  canvasRef?: React.RefObject<HTMLCanvasElement | null>
  /** When true, renders at 720p for streaming regardless of container size */
  streamingHeight?: number
  /** Called when the visible page changes (navigation or initial load). */
  onPageChange?: (page: number, totalPages: number) => void
  /**
   * Width/height of the document's first page, reported once it loads. Lets an
   * embedding container size itself to the deck instead of guessing a ratio and
   * letterboxing every document that disagrees.
   */
  onAspectRatio?: (ratio: number) => void
  /**
   * Embedded in a scrolling page rather than a modal. The page is always fit
   * whole (no width mode, which would need vertical scroll the host page has
   * already claimed), horizontal wheel/swipe turns pages, and the footer bar
   * gives way to controls floating over the page.
   */
  inline?: boolean
  /**
   * Arrow-key navigation. Turn it off when another viewer is stacked on top,
   * otherwise one key press moves both.
   */
  hotkeysEnabled?: boolean
  /**
   * 1-based page number (as a string key) → already-rendered slide image URL
   * (the same PNGs the deck's chat/vision context uses). Shown instantly in
   * place of the canvas — on first load and on every page turn — while
   * pdf.js still has to fetch, parse and rasterize that page itself. Absent
   * entirely for a non-deck document, or for a deck whose slides haven't
   * rendered yet.
   */
  placeholderImageUrls?: Record<string, string>
}

/** Pages within this many of the current one stay cached; farther ones are
 * evicted (their `ImageBitmap` closed) so paging through a long deck doesn't
 * grow memory unboundedly. */
const PAGE_CACHE_WINDOW = 3

interface CachedPage {
  bitmap: ImageBitmap
  /** Backing-store (hi-DPI) pixel size — what the canvas element's own
   * `width`/`height` get set to. */
  pixelWidth: number
  pixelHeight: number
  /** CSS size — what `canvas.style.width`/`height` get set to. */
  cssWidth: number
  cssHeight: number
}

function paintCachedPage(canvas: HTMLCanvasElement, cached: CachedPage) {
  canvas.width = cached.pixelWidth
  canvas.height = cached.pixelHeight
  canvas.style.width = `${cached.cssWidth}px`
  canvas.style.height = `${cached.cssHeight}px`
  canvas.getContext('2d')?.drawImage(cached.bitmap, 0, 0)
}

function evictFarPages(cache: Map<number, CachedPage>, centerPage: number) {
  for (const [page, cached] of cache) {
    if (Math.abs(page - centerPage) > PAGE_CACHE_WINDOW) {
      cached.bitmap.close()
      cache.delete(page)
    }
  }
}

/** Accumulated horizontal wheel delta that counts as one page turn. */
const WHEEL_PAGE_THRESHOLD = 60
/** Accumulated horizontal touch distance that counts as one page turn. */
const SWIPE_PAGE_THRESHOLD = 50
/** Trackpad flicks arrive as a burst of events; ignore the tail of one turn. */
const WHEEL_COOLDOWN_MS = 500

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs'

/**
 * Control floating over the rendered page, for the frameless inline viewer.
 * `visible` is driven by the container's own hover state (see `isHovering`
 * below) rather than a CSS `group-hover` class — this build's Tailwind/
 * Lightning CSS nesting output wasn't reliably scoping that selector to the
 * hovered ancestor, so the buttons stayed opaque all the time regardless of
 * hover.
 */
function OverlayButton({
  onClick,
  disabled,
  visible,
  label,
  className,
  children,
}: {
  onClick: () => void
  disabled?: boolean
  visible: boolean
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'absolute z-10 flex size-9 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur-sm transition-opacity hover:bg-black/80 focus-visible:opacity-100 disabled:pointer-events-none disabled:opacity-0',
        visible ? 'opacity-100' : 'opacity-0',
        className
      )}
    >
      {children}
    </button>
  )
}

type PDFDocumentProxy = Awaited<ReturnType<typeof pdfjs.getDocument>['promise']>

export function PdfViewer({
  url,
  className,
  footerAction,
  canvasRef: externalCanvasRef,
  streamingHeight,
  onPageChange,
  onAspectRatio,
  inline = false,
  hotkeysEnabled = true,
  placeholderImageUrls,
}: PdfViewerProps) {
  const [pdfDoc, setPdfDoc] = useState<PDFDocumentProxy | null>(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [error, setError] = useState<string | null>(null)
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 })
  const [storedFitMode, setFitMode] = useState<FitMode>('page')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  // The page currently painted on the VISIBLE canvas — null until the first
  // page ever finishes rendering. Distinct from `pageNumber` (the page the
  // viewer is trying to show) precisely during the render gap, which is what
  // the placeholder image covers.
  const [renderedPage, setRenderedPage] = useState<number | null>(null)
  const fitMode: FitMode = inline ? 'page' : storedFitMode

  const rootRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const internalCanvasRef = useRef<HTMLCanvasElement>(null)
  const canvasRef = externalCanvasRef ?? internalCanvasRef
  const renderTaskRef = useRef<ReturnType<pdfjs.PDFPageProxy['render']> | null>(null)
  const onPageChangeRef = useRef(onPageChange)
  onPageChangeRef.current = onPageChange
  const onAspectRatioRef = useRef(onAspectRatio)
  onAspectRatioRef.current = onAspectRatio

  // Rendered pages, keyed by page number, at the CURRENT scale — a page turn
  // that lands on an already-cached page paints instantly instead of paying
  // for pdf.js's async render again. `PAGE_CACHE_WINDOW` bounds it so a
  // 100-slide deck someone has clicked all the way through doesn't hold
  // every page's bitmap in memory forever.
  const pageCacheRef = useRef<Map<number, CachedPage>>(new Map())

  // Observe container size
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        const { width, height } = entry.contentRect
        setContainerSize({ width, height })
      }
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [])

  // Load PDF document
  useEffect(() => {
    let cancelled = false

    setError(null)
    setPdfDoc(null)
    setPageNumber(1)
    setRenderedPage(null)

    pdfjs
      .getDocument(url)
      .promise.then((doc) => {
        if (cancelled) return
        setPdfDoc(doc)
        onPageChangeRef.current?.(1, doc.numPages)
        if (onAspectRatioRef.current) {
          doc.getPage(1).then((page) => {
            if (cancelled) return
            const { width, height } = page.getViewport({ scale: 1 })
            if (height > 0) onAspectRatioRef.current?.(width / height)
          })
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load PDF')
        }
      })

    return () => {
      cancelled = true
    }
  }, [url])

  // Scale is a function of the document + container box, not of which page
  // is showing — every cached bitmap is invalid the instant any of these
  // change, so the whole cache drops rather than trying to guess which
  // entries might still match.
  // biome-ignore lint/correctness/useExhaustiveDependencies: empty body — only exists to run its cleanup when these deps change
  useEffect(() => {
    return () => {
      for (const cached of pageCacheRef.current.values()) cached.bitmap.close()
      pageCacheRef.current.clear()
    }
  }, [pdfDoc, containerSize, streamingHeight, fitMode])

  // Renders one page to an off-screen canvas and returns it as an
  // `ImageBitmap` — used both for the page on screen right now and for
  // quietly warming its neighbors. `onTaskStarted` fires synchronously,
  // before the first `await`, so a caller that needs to cancel an
  // in-flight render (the visible page does; prefetches don't bother) can
  // capture the task.
  const rasterizePage = useCallback(
    async (
      page: number,
      onTaskStarted?: (task: ReturnType<pdfjs.PDFPageProxy['render']>) => void
    ): Promise<CachedPage | null> => {
      if (!pdfDoc || containerSize.width === 0) return null

      const pdfPage = await pdfDoc.getPage(page)
      const unscaledViewport = pdfPage.getViewport({ scale: 1 })
      let scale: number

      if (streamingHeight) {
        scale = streamingHeight / unscaledViewport.height
      } else {
        const scaleX = containerSize.width / unscaledViewport.width
        if (fitMode === 'width') {
          scale = scaleX
        } else {
          const scaleY = containerSize.height / unscaledViewport.height
          scale = Math.min(scaleX, scaleY)
        }
      }

      const dpr = window.devicePixelRatio || 1
      const viewport = pdfPage.getViewport({ scale })
      const hiResViewport = pdfPage.getViewport({ scale: scale * dpr })

      const offscreen = document.createElement('canvas')
      offscreen.width = hiResViewport.width
      offscreen.height = hiResViewport.height
      const ctx = offscreen.getContext('2d', { willReadFrequently: true })
      if (!ctx) return null

      const renderTask = pdfPage.render({
        canvasContext: ctx,
        viewport: hiResViewport,
        canvas: offscreen,
      })
      onTaskStarted?.(renderTask)
      await renderTask.promise

      const bitmap = await createImageBitmap(offscreen)
      return {
        bitmap,
        pixelWidth: hiResViewport.width,
        pixelHeight: hiResViewport.height,
        cssWidth: viewport.width,
        cssHeight: viewport.height,
      }
    },
    [pdfDoc, containerSize, streamingHeight, fitMode]
  )

  // Show `pageNumber` — instantly if it's already cached (the fast path a
  // successful prefetch below sets up), otherwise render it now.
  // biome-ignore lint/correctness/useExhaustiveDependencies: canvasRef is a stable RefObject
  useEffect(() => {
    const canvas = canvasRef.current
    if (!pdfDoc || !canvas || containerSize.width === 0) return

    const cached = pageCacheRef.current.get(pageNumber)
    if (cached) {
      paintCachedPage(canvas, cached)
      setRenderedPage(pageNumber)
      evictFarPages(pageCacheRef.current, pageNumber)
      return
    }

    let cancelled = false
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel()
      renderTaskRef.current = null
    }

    rasterizePage(pageNumber, (task) => {
      renderTaskRef.current = task
    })
      .then((result) => {
        if (cancelled || !result) return
        pageCacheRef.current.set(pageNumber, result)
        paintCachedPage(canvas, result)
        setRenderedPage(pageNumber)
        evictFarPages(pageCacheRef.current, pageNumber)
      })
      .catch((err) => {
        if (err.name !== 'RenderingCancelledException') {
          console.error('PDF render error:', err)
        }
      })

    return () => {
      cancelled = true
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel()
        renderTaskRef.current = null
      }
    }
  }, [pdfDoc, pageNumber, containerSize, rasterizePage])

  // Quietly render the pages either side of the current one, so a page turn
  // usually finds its target already in `pageCacheRef` and paints instantly
  // via the effect above, instead of paying for pdf.js's async render on
  // every click.
  useEffect(() => {
    if (!pdfDoc || containerSize.width === 0) return
    const numPages = pdfDoc.numPages
    const neighbors = [pageNumber + 1, pageNumber - 1].filter((p) => p >= 1 && p <= numPages)

    let cancelled = false
    for (const page of neighbors) {
      if (pageCacheRef.current.has(page)) continue
      rasterizePage(page)
        .then((result) => {
          if (cancelled || !result || pageCacheRef.current.has(page)) return
          pageCacheRef.current.set(page, result)
        })
        .catch(() => {
          // Best-effort warm-up — the main effect will render this page
          // properly (and surface any real error) if the user pages to it.
        })
    }
    return () => {
      cancelled = true
    }
  }, [pdfDoc, pageNumber, containerSize, rasterizePage])

  const goToPreviousPage = useCallback(() => {
    setPageNumber((prev) => {
      const next = Math.max(prev - 1, 1)
      if (pdfDoc) onPageChangeRef.current?.(next, pdfDoc.numPages)
      return next
    })
    containerRef.current?.scrollTo(0, 0)
  }, [pdfDoc])

  const goToNextPage = useCallback(() => {
    if (!pdfDoc) return
    setPageNumber((prev) => {
      const next = Math.min(prev + 1, pdfDoc.numPages)
      onPageChangeRef.current?.(next, pdfDoc.numPages)
      return next
    })
    containerRef.current?.scrollTo(0, 0)
  }, [pdfDoc])

  useHotkeys('left', goToPreviousPage, { enabled: hotkeysEnabled })
  useHotkeys('right', goToNextPage, { enabled: hotkeysEnabled })

  // Horizontal gestures turn pages; vertical ones are left alone so the page
  // this viewer is embedded in keeps scrolling normally. Registered manually
  // because the turn has to preventDefault (otherwise the browser reads the
  // same flick as a back-navigation swipe) and React's wheel listener is
  // passive.
  useEffect(() => {
    const root = rootRef.current
    if (!inline || !root) return

    let accumulated = 0
    let lockedUntil = 0
    let touchStart: { x: number; y: number } | null = null

    const turn = (delta: number) => {
      if (delta > 0) goToNextPage()
      else goToPreviousPage()
    }

    const onWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return
      event.preventDefault()
      const now = performance.now()
      if (now < lockedUntil) return
      accumulated += event.deltaX
      if (Math.abs(accumulated) < WHEEL_PAGE_THRESHOLD) return
      turn(accumulated)
      accumulated = 0
      lockedUntil = now + WHEEL_COOLDOWN_MS
    }

    const onTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0]
      touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null
    }

    const onTouchEnd = (event: TouchEvent) => {
      const touch = event.changedTouches[0]
      if (!touchStart || !touch) return
      const dx = touch.clientX - touchStart.x
      const dy = touch.clientY - touchStart.y
      touchStart = null
      if (Math.abs(dx) < SWIPE_PAGE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return
      turn(dx * -1)
    }

    root.addEventListener('wheel', onWheel, { passive: false })
    root.addEventListener('touchstart', onTouchStart, { passive: true })
    root.addEventListener('touchend', onTouchEnd, { passive: true })
    return () => {
      root.removeEventListener('wheel', onWheel)
      root.removeEventListener('touchstart', onTouchStart)
      root.removeEventListener('touchend', onTouchEnd)
    }
  }, [inline, goToNextPage, goToPreviousPage])

  // Esc and the browser's own fullscreen chrome exit without going through the
  // button, so the state has to follow the document, not the click.
  useEffect(() => {
    const sync = () => setIsFullscreen(document.fullscreenElement === rootRef.current)
    document.addEventListener('fullscreenchange', sync)
    return () => document.removeEventListener('fullscreenchange', sync)
  }, [])

  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      rootRef.current?.requestFullscreen()
    }
  }, [])

  const numPages = pdfDoc?.numPages ?? null
  const placeholderUrl = placeholderImageUrls?.[String(pageNumber)]
  // The canvas doesn't show this page yet — either nothing has ever painted
  // (first load) or a page turn is still in flight (double-buffered, so the
  // previous page's pixels are what's actually on screen right now).
  const isPagePending = renderedPage !== pageNumber

  return (
    <div
      ref={rootRef}
      className={cn(
        'flex h-full min-h-0 flex-col',
        // Inline sits inside the profile card and borrows its background, so a
        // frame of its own would read as a second card. Fullscreen has no card
        // to sit in and needs its own ground.
        inline && !isFullscreen ? 'bg-transparent' : 'bg-zinc-900',
        className
      )}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: purely a visual
          hover affordance (show/hide the floating page controls), not a
          keyboard/AT interaction — the buttons themselves stay reachable and
          operable via focus-visible regardless of hover state. */}
      <div
        ref={containerRef}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        className={cn(
          'relative flex min-h-0 flex-1 justify-center',
          fitMode === 'width' ? 'items-start overflow-y-auto' : 'items-center overflow-hidden'
        )}
      >
        {/* Only shown when there is truly nothing else to look at: no
            placeholder image for this page, and the canvas has never
            painted anything (a page turn instead just leaves the previous
            page's pixels in place underneath, via the double buffer). */}
        {!error && !placeholderUrl && renderedPage === null && (
          <div className="absolute flex flex-col items-center gap-3 text-zinc-400">
            <FileText className="size-12 animate-pulse" />
            <span>Loading document...</span>
          </div>
        )}

        {error && (
          <div className="absolute flex flex-col items-center gap-3 text-zinc-400">
            <FileText className="size-12" />
            <span className="text-red-400">{error}</span>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="shadow-2xl"
          style={
            streamingHeight
              ? { maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }
              : undefined
          }
        />

        {/* Instant paint for the page pdf.js hasn't finished rendering yet —
            already-rendered slide art, shown on top of (or instead of) the
            canvas so first load and every page turn never show blank/white. */}
        {placeholderUrl && isPagePending && (
          <img
            src={placeholderUrl}
            alt=""
            aria-hidden
            className="absolute inset-0 size-full object-contain"
          />
        )}

        {/* Inline controls float over the page rather than sitting in a footer
            bar, which would be a second frame inside the profile card. */}
        {inline && numPages && numPages > 0 && (
          <>
            <OverlayButton
              onClick={goToPreviousPage}
              disabled={pageNumber <= 1}
              visible={isHovering}
              label="Previous page"
              className="left-2 top-1/2 -translate-y-1/2"
            >
              <ArrowLeft className="size-4" />
            </OverlayButton>

            <OverlayButton
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              visible={isHovering}
              label="Next page"
              className="right-2 top-1/2 -translate-y-1/2"
            >
              <ArrowRight className="size-4" />
            </OverlayButton>

            <OverlayButton
              onClick={toggleFullscreen}
              visible={isHovering}
              label={isFullscreen ? 'Exit full screen' : 'Full screen'}
              className="right-2 top-2"
            >
              {isFullscreen ? <Shrink className="size-4" /> : <Expand className="size-4" />}
            </OverlayButton>
          </>
        )}
      </div>

      {!inline && ((numPages && numPages > 0) || footerAction) ? (
        <div
          className={cn(
            'flex items-center border-t border-zinc-700 bg-zinc-800/80 px-4 py-2 backdrop-blur-sm',
            footerAction ? 'justify-between' : 'justify-center'
          )}
        >
          {numPages && numPages > 0 ? (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={goToPreviousPage}
                disabled={pageNumber <= 1}
                className="size-8 p-0 focus-visible:ring-0 focus-visible:border-border"
              >
                <ArrowLeft className="size-4" />
              </Button>

              <span className="min-w-[60px] text-center text-xs font-medium text-zinc-300">
                {pageNumber} / {numPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={goToNextPage}
                disabled={pageNumber >= numPages}
                className="size-8 p-0 focus-visible:ring-0 focus-visible:border-border"
              >
                <ArrowRight className="size-4" />
              </Button>

              {!streamingHeight && (
                <>
                  <div className="ml-1 h-4 w-px bg-zinc-600" />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setFitMode((m) => (m === 'page' ? 'width' : 'page'))}
                    className="size-8 p-0 focus-visible:ring-0 focus-visible:border-border"
                    title={fitMode === 'page' ? 'Full width' : 'Full page'}
                  >
                    {fitMode === 'page' ? (
                      <Maximize2 className="size-4" />
                    ) : (
                      <Minimize2 className="size-4" />
                    )}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div />
          )}

          {footerAction}
        </div>
      ) : null}
    </div>
  )
}
